// The onGuard tile mosaic as a set of small, quiet brand ELEMENTS (ENTRAID-347).
// It is never a logo and never one big picture dropped on a page: the A2 Pulse
// shield stays the only symbol. The elements follow how IPC Global's own
// Office templates use squares (PrezDesign3 deck, proposal Word template):
//
// - square, unrounded tiles on a strict grid; gap about one fifth of the pitch
//   (tile 10 on pitch 12, the same 0.83 ratio as the onGuard mark);
// - tone ordered by row: light blue on top, IPC blue, navy, then a pale mist;
// - density grows toward an edge and dissolves away from it (sparse tiles on
//   top of a dense row);
// - anchored to an edge or corner and bleeding off it, never floating;
// - tone-on-tone (a tile one step off the surface color) when it sits on a
//   colored panel behind content.
//
// Every element is pure SVG: aria-hidden, not focusable, no pointer events,
// no text, crisp edges (whole-pixel pitch, shape-rendering crispEdges), solid
// fills from the --mosaic-* tokens in styles.css (light surface by default,
// the dark set under .on-dark, [data-theme="dark"] or tone="dark"). Placement
// is the caller's job; the elements never take layout space except the band.
// A short fade-in plays unless the user prefers reduced motion.

// Tone roles, mapped to classes and tokens in styles.css.
// b1 sky (#5BB4F0), b2 navy, b3 IPC blue (#0066B3), b4 mist, b5 tint.
const SKY = 'b1';
const NAVY = 'b2';
const BLUE = 'b3';
const MIST = 'b4';
const TINT = 'b5';

const tileOf = (pitch) => Math.max(1, Math.round(pitch * 0.83));

// Deterministic 0..1 noise per grid cell, so a render is stable (no flicker,
// no hydration mismatch, the same picture in every screenshot).
function noise(c, r, seed = 1) {
  let h = Math.imul(c + 1, 374761393) ^ Math.imul(r + 1, 668265263) ^ Math.imul(seed, 2246822519);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

function svgProps(name, tone, className) {
  return {
    'aria-hidden': 'true',
    focusable: 'false',
    'data-mosaic': name,
    'data-tone': tone,
    shapeRendering: 'crispEdges',
    className: `mosaic ${className}`.trim(),
  };
}

function Tiles({ cells, pitch }) {
  const tile = tileOf(pitch);
  return cells.map(([slot, c, r]) => (
    <rect key={`${c}-${r}`} className={`mosaic-${slot}`} x={c * pitch} y={r * pitch} width={tile} height={tile} />
  ));
}

/**
 * MosaicPattern: a very low-contrast field of tint tiles that fills its
 * (relative, overflow-hidden) parent, densest at the bottom edge and
 * dissolving upward. Tint is one step off the surface color, so text placed
 * over it keeps its contrast.
 * reach is the share of the field height (from the bottom) that holds tiles.
 * @param {{pitch?: number, cols?: number, rows?: number, reach?: number, tone?: 'light'|'dark', className?: string}} props
 */
export function MosaicPattern({ pitch = 24, cols = 64, rows = 40, reach = 0.5, tone, className = '' }) {
  const cells = [];
  for (let r = 0; r < rows; r += 1) {
    // 0 at the bottom edge; nothing above the lower `reach` of the field.
    const fromBottom = (rows - 1 - r) / ((rows - 1) * reach);
    const density = fromBottom >= 1 ? 0 : 0.5 * (1 - fromBottom) ** 2.4;
    for (let c = 0; c < cols; c += 1) {
      if (noise(c, r, 7) < density) cells.push([TINT, c, r]);
    }
  }
  return (
    <div aria-hidden="true" className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`.trim()}>
      <svg
        {...svgProps('pattern', tone, 'absolute bottom-0 left-0')}
        width={cols * pitch}
        height={rows * pitch}
        viewBox={`0 0 ${cols * pitch} ${rows * pitch}`}
      >
        <Tiles cells={cells} pitch={pitch} />
      </svg>
    </div>
  );
}

// One 24-column repeat of the band, rows ordered like the IPC cover band:
// a sparse light row over a dense blue row.
function bandCells(cols, rows) {
  const cells = [];
  for (let c = 0; c < cols; c += 1) {
    if (rows > 1 && noise(c, 0, 3) < 0.3) cells.push([SKY, c, 0]);
    if (noise(c, 1, 3) < 0.72) cells.push([rows > 1 ? BLUE : SKY, c, rows - 1]);
  }
  return cells;
}

/**
 * MosaicBand: a thin horizontal strip of tiles (1 or 2 rows) that runs the
 * full width of its parent. Use it at a panel boundary or under a header.
 * @param {{pitch?: number, rows?: 1|2, tone?: 'light'|'dark', className?: string}} props
 */
export function MosaicBand({ pitch = 10, rows = 2, tone, className = '' }) {
  const repeat = 24;
  const id = `mosaic-band-${pitch}-${rows}`;
  return (
    <svg
      {...svgProps('band', tone, `block w-full pointer-events-none ${className}`)}
      height={rows * pitch}
    >
      <defs>
        <pattern id={id} width={repeat * pitch} height={rows * pitch} patternUnits="userSpaceOnUse">
          <Tiles cells={bandCells(repeat, rows)} pitch={pitch} />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#${id})`} />
    </svg>
  );
}

// The corner cluster of the IPC content slides, bottom-right orientation:
// a light tile on top, blue, two navy, mist at the foot, bleeding off the edge.
const CORNER = [
  [SKY, 2, 0],
  [BLUE, 1, 1],
  [NAVY, 1, 2], [NAVY, 2, 2],
  [MIST, 2, 3],
  [MIST, 0, 4], [MIST, 1, 4], [MIST, 2, 4],
];
const CORNER_COLS = 3;
const CORNER_ROWS = 5;
const CORNER_PLACE = {
  'bottom-right': 'bottom-0 right-0',
  'bottom-left': 'bottom-0 left-0',
  'top-right': 'top-0 right-0',
  'top-left': 'top-0 left-0',
};

/**
 * MosaicCorner: a few tiles anchored to a corner of a relative parent,
 * flush with both edges. Keep the parent's padding at least as wide as the
 * cluster so it never sits behind text.
 * @param {{corner?: 'bottom-right'|'bottom-left'|'top-right'|'top-left', pitch?: number, tone?: 'light'|'dark', className?: string}} props
 */
export function MosaicCorner({ corner = 'bottom-right', pitch = 14, tone, className = '' }) {
  const tile = tileOf(pitch);
  const w = (CORNER_COLS - 1) * pitch + tile;
  const h = (CORNER_ROWS - 1) * pitch + tile;
  const flipX = corner.endsWith('left');
  const flipY = corner.startsWith('top');
  const cells = CORNER.map(([slot, c, r]) => [
    slot,
    flipX ? CORNER_COLS - 1 - c : c,
    flipY ? CORNER_ROWS - 1 - r : r,
  ]);
  return (
    <svg
      {...svgProps('corner', tone, `pointer-events-none absolute ${CORNER_PLACE[corner]} ${className}`)}
      data-corner={corner}
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
    >
      <Tiles cells={cells} pitch={pitch} />
    </svg>
  );
}

/**
 * MosaicDivider: a narrow vertical strip on a panel boundary, two tiles wide,
 * dense at the bottom and dissolving upward. Place it in a relative parent;
 * side picks the edge it sits on; reach is the share of the height that holds tiles.
 * @param {{side?: 'left'|'right', pitch?: number, rows?: number, reach?: number, tone?: 'light'|'dark', className?: string}} props
 */
export function MosaicDivider({ side = 'right', pitch = 12, rows = 90, reach = 0.4, tone, className = '' }) {
  const cells = [];
  for (let r = 0; r < rows; r += 1) {
    const up = (rows - 1 - r) / ((rows - 1) * reach); // 0 at the bottom
    for (let c = 0; c < 2; c += 1) {
      const outer = side === 'right' ? c === 1 : c === 0;
      const density = up >= 1 ? 0 : (outer ? 0.9 : 0.2) * (1 - up) ** 2;
      if (noise(c, r, 11) < density) cells.push([outer ? BLUE : SKY, c, r]);
    }
  }
  const w = pitch + tileOf(pitch);
  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute inset-y-0 ${side === 'right' ? 'right-0' : 'left-0'} overflow-hidden ${className}`.trim()}
      style={{ width: w }}
    >
      <svg {...svgProps('divider', tone, 'absolute bottom-0 left-0')} width={w} height={rows * pitch} viewBox={`0 0 ${w} ${rows * pitch}`}>
        <Tiles cells={cells} pitch={pitch} />
      </svg>
    </div>
  );
}

// The small inline cluster for app chrome: a two-row fragment of the IPC
// cover band (sparse light tiles over a blue row), fixed size.
const CLUSTER = [
  [SKY, 1, 0], [SKY, 4, 0],
  [BLUE, 0, 1], [BLUE, 1, 1], [BLUE, 2, 1], [BLUE, 4, 1], [BLUE, 5, 1],
];

/**
 * Mosaic cluster: a small inline illustration for the top bar.
 * @param {{tone?: 'light'|'dark', className?: string}} props
 */
export default function Mosaic({ tone, className = '' }) {
  const pitch = 8;
  const tile = tileOf(pitch);
  const w = 5 * pitch + tile;
  const h = pitch + tile;
  return (
    <svg
      {...svgProps('cluster', tone, `block shrink-0 ${className}`)}
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
    >
      <Tiles cells={CLUSTER} pitch={pitch} />
    </svg>
  );
}
